// src/features/premium/lojaPlanos.js
//
// A loja (RevenueCat) vista pelo paywall novo (PlanosAssinatura). Tudo aqui lê
// CAMPOS ESTRUTURADOS do SDK — packageType, identifier do package,
// product.subscriptionPeriod, introPrice, defaultOption.freePhase,
// installmentsInfo — e nunca números de mock.
//
// De onde veio cada regra (sem alterar os originais):
//   • classificação por período, à vista (P1Y) × parcelado (annual_installments,
//     base plan `lesvita-start-plan-anual`) × mensal ($rc_monthly):
//     Sliders.jsx › loadPlans (ramo ANDROID) e loadIOSFromRevenueCat;
//   • compra: SubscribeButton.jsx › buyWithStore (logIn com o uid do Firebase
//     ANTES de comprar, atributo affiliate_code, purchasePackage, cancelamento
//     do usuário não é erro).
//
// Unidades: `product.price` é na moeda (ex. 226.8). Os `pricePer*` variam de
// unidade entre plataformas (o Sliders viu micros no Android), então NÃO são
// usados para conta: equivalente mensal = price / 12; total do parcelado =
// parcela × número de pagamentos do compromisso.

import { Capacitor } from '@capacitor/core';
import { Purchases } from '@revenuecat/purchases-capacitor';
import { API_BASE_URL } from '../../config/api';

const DIAG = process.env.NODE_ENV !== 'production';
const log = (...a) => { if (DIAG) console.log('[PLANOS]', ...a); };

// ── Formatação ───────────────────────────────────────────────

/** Preço em pt-BR na moeda da loja (BRL quando a loja não disser). */
export function formatarPreco(valor, moeda = 'BRL') {
  const n = Number(valor);
  if (!Number.isFinite(n)) return '';
  try {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: moeda || 'BRL' }).format(n);
  } catch {
    return `R$ ${n.toFixed(2).replace('.', ',')}`;
  }
}

/**
 * Economia do anual sobre o mensal, em % inteiro (arredondado para baixo, para
 * nunca prometer mais do que a conta dá). null quando não há o que comparar.
 */
export function economiaPercentual(totalAnual, precoMensal) {
  const a = Number(totalAnual);
  const m = Number(precoMensal);
  if (!(a > 0) || !(m > 0)) return null;
  const pct = Math.floor((1 - (a / 12) / m) * 100);
  return pct >= 1 ? pct : null;
}

// ── Duração do teste ─────────────────────────────────────────

const UNIDADES = {
  DAY: ['dia', 'dias', 1],
  WEEK: ['semana', 'semanas', 7],
  MONTH: ['mês', 'meses', 30],
  YEAR: ['ano', 'anos', 365],
};

/**
 * Monta a duração do teste a partir de unidade + quantidade da loja.
 * Semana vira dias ("7 dias"), que é como o resto do funil fala.
 */
export function duracaoTeste(unidade, quantidade) {
  const u = String(unidade || '').toUpperCase();
  const q = Number(quantidade) || 0;
  if (!UNIDADES[u] || q <= 0) return null;
  const dias = q * UNIDADES[u][2];
  if (u === 'DAY' || u === 'WEEK') {
    return { dias, rotulo: `${dias} ${dias === 1 ? 'dia' : 'dias'}` };
  }
  const [um, varios] = UNIDADES[u];
  return { dias, rotulo: `${q} ${q === 1 ? um : varios}` };
}

// iOS: introPrice com preço 0 é teste grátis (preço > 0 é desconto, não teste).
function testeIOS(product) {
  const intro = product?.introPrice;
  if (!intro || Number(intro.price) !== 0) return null;
  const ciclos = Number(intro.cycles) || 1;
  return duracaoTeste(intro.periodUnit, (Number(intro.periodNumberOfUnits) || 0) * ciclos);
}

// Android: a defaultOption já é a oferta que o Google liberou para ESTE
// usuário; freePhase presente = teste disponível.
function testeAndroid(product) {
  const fase = product?.defaultOption?.freePhase;
  if (!fase) return null;
  const ciclos = Number(fase.billingCycleCount) || 1;
  return duracaoTeste(fase.billingPeriod?.unit, (Number(fase.billingPeriod?.value) || 0) * ciclos);
}

// ── Classificação dos pacotes ────────────────────────────────

const periodoDe = (p) => p?.product?.subscriptionPeriod || '';
const idProduto = (p) => String(p?.product?.identifier || '').toLowerCase();

/** Parcelado do Google Play (BR): package annual_installments / installmentsInfo. */
export function ehParcelado(p) {
  if (!p) return false;
  if (p.identifier === 'annual_installments') return true;
  if (p.product?.defaultOption?.installmentsInfo) return true;
  // Base plan de parcelamento — o mesmo critério do Sliders.
  return idProduto(p).includes('lesvita-start-plan-anual');
}

function ehAnual(p) {
  if (!p || ehParcelado(p)) return false;
  if (periodoDe(p) === 'P1Y') return true;
  const tipo = p.packageType === 'ANNUAL' || p.identifier === '$rc_annual';
  return tipo && periodoDe(p) !== 'P1M';
}

function ehMensal(p) {
  if (!p || ehParcelado(p)) return false;
  if (p.identifier === '$rc_monthly' || p.packageType === 'MONTHLY') return true;
  return periodoDe(p) === 'P1M';
}

/**
 * Separa os pacotes do offering em { anual, parcelado, mensal }. Prioriza o
 * período real do produto (P1Y) sobre o nome do package.
 */
export function classificarPacotes(pacotes = [], plataforma = Capacitor.getPlatform()) {
  const anual =
    pacotes.find((p) => !ehParcelado(p) && periodoDe(p) === 'P1Y') ||
    pacotes.find(ehAnual) ||
    null;
  const mensal =
    pacotes.find((p) => !ehParcelado(p) && (p.identifier === '$rc_monthly' || p.packageType === 'MONTHLY')) ||
    pacotes.find(ehMensal) ||
    null;
  // Parcelado é coisa do Google Play; no iOS nem procura.
  const parcelado = plataforma === 'android' ? pacotes.find(ehParcelado) || null : null;
  return { anual, parcelado, mensal };
}

// ── Montagem dos planos ──────────────────────────────────────

function numeroDeParcelas(product) {
  const opcoes = [product?.defaultOption, ...(product?.subscriptionOptions || [])];
  const info = opcoes.find((o) => o?.installmentsInfo)?.installmentsInfo;
  const n = Number(info?.commitmentPaymentsCount);
  return n > 0 ? n : 12;
}

function montarPlano(pacote, tipo, teste) {
  if (!pacote) return null;
  const product = pacote.product || {};
  const moeda = product.currencyCode || 'BRL';
  const preco = Number(product.price);
  const base = {
    tipo,
    pacote,
    packageId: pacote.identifier,
    productId: product.identifier,
    moeda,
    teste: teste || null,
  };

  if (tipo === 'parcelado') {
    const parcelas = numeroDeParcelas(product);
    const total = preco * parcelas;
    return {
      ...base,
      parcelas,
      parcela: preco,
      total,
      porMes: preco,
      parcelaTexto: formatarPreco(preco, moeda),
      totalTexto: formatarPreco(total, moeda),
      porMesTexto: formatarPreco(preco, moeda),
    };
  }

  if (tipo === 'anual') {
    return {
      ...base,
      total: preco,
      porMes: preco / 12,
      totalTexto: formatarPreco(preco, moeda),
      porMesTexto: formatarPreco(preco / 12, moeda),
    };
  }

  return {
    ...base,
    total: preco,
    porMes: preco,
    totalTexto: formatarPreco(preco, moeda),
    porMesTexto: formatarPreco(preco, moeda),
  };
}

async function elegibilidadeIOS(produtos) {
  const ids = produtos.filter(Boolean).map((p) => p.identifier);
  if (!ids.length) return {};
  try {
    const r = await Purchases.checkTrialOrIntroductoryPriceEligibility({ productIdentifiers: ids });
    return r || {};
  } catch (e) {
    // Sem resposta = UNKNOWN = trata como NÃO elegível (nunca prometer teste).
    log('checkTrialOrIntroductoryPriceEligibility falhou', e);
    return {};
  }
}

// INTRO_ELIGIBILITY_STATUS_ELIGIBLE
const ELEGIVEL = 2;

/**
 * Carrega os planos da loja (nativo). Resolve com
 *   { offeringId, anual, parcelado, mensal }
 * cada um no formato de `montarPlano` (ou null). Lança se o offering não trouxer
 * nem anual nem mensal.
 */
export async function carregarPlanosDaLoja(plataforma = Capacitor.getPlatform()) {
  const offerings = await Purchases.getOfferings();
  const current = offerings?.current;
  const pacotes = current?.availablePackages || [];
  const { anual, parcelado, mensal } = classificarPacotes(pacotes, plataforma);

  log('offering', current?.identifier, pacotes.map((p) => ({
    package: p?.identifier, type: p?.packageType, product: p?.product?.identifier, period: periodoDe(p),
  })));

  if (!anual && !mensal) throw new Error('Offering sem planos anual/mensal.');

  let teste = (p) => testeAndroid(p?.product);
  if (plataforma === 'ios') {
    const status = await elegibilidadeIOS([anual?.product, mensal?.product]);
    teste = (p) => {
      if (!p) return null;
      const s = status[p.product?.identifier];
      return s && Number(s.status) === ELEGIVEL ? testeIOS(p.product) : null;
    };
  }

  return {
    offeringId: current?.identifier || null,
    anual: montarPlano(anual, 'anual', teste(anual)),
    parcelado: montarPlano(parcelado, 'parcelado', teste(parcelado)),
    mensal: montarPlano(mensal, 'mensal', teste(mensal)),
  };
}

/**
 * Web: o checkout é o /assinar, que vende SÓ o anual e lê o preço de
 * /pay/checkout/planos (plano "promo" é o padrão da rota). Mesma fonte, mesmos
 * números.
 */
export async function carregarPlanoWeb() {
  const r = await fetch(`${API_BASE_URL}/pay/checkout/planos`);
  if (!r.ok) throw new Error(`/pay/checkout/planos ${r.status}`);
  const d = await r.json();
  const p = d?.PLANOS?.promo;
  if (!p || !(Number(p.TOTAL_CENTAVOS) > 0)) throw new Error('Plano web ausente.');
  const parcelas = Number(p.PARCELAS) || 12;
  const total = Number(p.TOTAL_CENTAVOS) / 100;
  const parcela = Number(p.PARCELA_CENTAVOS) > 0 ? Number(p.PARCELA_CENTAVOS) / 100 : total / parcelas;
  return {
    tipo: 'web',
    moeda: 'BRL',
    parcelas,
    parcela,
    total,
    parcelaTexto: formatarPreco(parcela),
    totalTexto: formatarPreco(total),
  };
}

// ── Compra, restauração e leitura do customerInfo ────────────

/** Cancelamento pelo usuário — não é erro (mesma régua do SubscribeButton). */
export function compraCancelada(e) {
  return e?.userCancelled === true || e?.code === '1' || e?.code === 1 || e?.code === 'PURCHASE_CANCELLED';
}

/**
 * Compra um package do offering. Vincula ao uid do Firebase ANTES (senão a
 * compra cai num usuário anônimo do RevenueCat e o webhook não acha a conta).
 * @returns {Promise<object>} customerInfo
 */
export async function comprarPacote(pacote, { uid, codigoAfiliado } = {}) {
  if (!uid) throw new Error('Sessão inválida. Saia e entre de novo na sua conta.');
  if (!pacote) throw new Error('Plano indisponível, tente novamente.');

  try {
    await Purchases.logIn({ appUserID: uid });
  } catch (e) {
    console.warn('[RC] Falha no logIn (seguindo mesmo assim):', e);
  }

  try {
    await Purchases.setAttributes({ affiliate_code: (codigoAfiliado || '').trim() });
  } catch (e) {
    console.warn('[RC] Aviso ao definir atributos:', e);
  }

  log('purchasePackage →', { package: pacote.identifier, product: pacote.product?.identifier });
  const { customerInfo } = await Purchases.purchasePackage({ aPackage: pacote });
  return customerInfo;
}

/** Alguma entitlement ativa está no período de teste? */
export function estaEmTeste(customerInfo) {
  const ativas = Object.values(customerInfo?.entitlements?.active || {});
  return ativas.some((e) => String(e?.periodType || '').toUpperCase() === 'TRIAL');
}

/** Há alguma entitlement ativa? */
export function temAssinaturaAtiva(customerInfo) {
  return Object.keys(customerInfo?.entitlements?.active || {}).length > 0;
}

/** Restaurar compras (só nativo). @returns {Promise<object>} customerInfo */
export async function restaurarCompras() {
  const r = await Purchases.restorePurchases();
  return r?.customerInfo || null;
}

/** RevenueCat não configurado no boot (AppProviders marca __RC_READY__ = false). */
export function lojaIndisponivel() {
  return typeof window !== 'undefined' && window.__RC_READY__ === false;
}

// ── Textos que dependem da loja ──────────────────────────────

export const URL_TERMOS = 'https://www.lesvita.com/termos';
export const URL_PRIVACIDADE = 'https://www.lesvita.com/privacidade';

/**
 * Nome da loja e onde se cancela. Guideline 2.3.10: o binário iOS não pode
 * citar a loja do Android e vice-versa (mesma regra do PaywallStoreFooter).
 */
export function textosDaLoja(plataforma = Capacitor.getPlatform()) {
  if (plataforma === 'ios') {
    return { loja: 'App Store', ondeCancelar: 'nos Ajustes da sua conta Apple ID', naLoja: 'na App Store' };
  }
  if (plataforma === 'android') {
    return { loja: 'Google Play', ondeCancelar: 'na sua conta do Google Play', naLoja: 'no Google Play' };
  }
  return { loja: 'loja', ondeCancelar: 'na loja', naLoja: 'na loja' };
}
